import React, { useState, useEffect, useMemo } from 'react';
import { Chessboard } from 'react-chessboard';
import { Chess } from 'chess.js';

/**
 * BlogChessBoard Component
 *
 * Interactive chess board for blog posts with 3 modes:
 * - EDIT: User can make moves on board, updates PGN, has undo button
 * - PREVIEW: User can explore variations freely, has reset button
 * - LIVE: Same as preview, for published blogs
 *
 * Props:
 * - initialFen: Starting FEN position (optional, defaults to start position)
 * - initialPgn: Initial PGN string (optional)
 * - showMoves: Whether to show moves immediately (true) or hide behind button (false)
 * - mode: 'edit' | 'preview' | 'live'
 * - onChange: Callback for PGN changes (edit mode only)
 */

const BlogChessBoard = ({
  initialFen = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
  initialPgn = '',
  initialTitle = '',
  showMoves = true,
  mode = 'live',
  onChange,
  hideControls = false
}) => {
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on mount
    checkMobile();

    // Listen for resize
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to extract just the moves from PGN (strip headers)
  const extractMovesFromPgn = (fullPgn) => {
    if (!fullPgn) return '';

    // Split by newlines and find the last non-empty line (the moves)
    const lines = fullPgn.split('\n').filter(line => line.trim() !== '');
    const movesLine = lines[lines.length - 1] || '';

    // Remove result markers like *, 1-0, 0-1, 1/2-1/2
    return movesLine.replace(/\s*[\*1\-0\/2]+\s*$/, '').trim();
  };

  // Create chess instance
  const [chess] = useState(() => {
    const game = new Chess();
    if (initialFen && initialFen !== game.fen()) {
      try {
        game.load(initialFen);
      } catch (err) {
        console.error('Invalid FEN, using start position:', err);
      }
    }
    return game;
  });

  // Parse moves from PGN
  const parsedMoves = useMemo(() => {
    if (!initialPgn || initialPgn.trim() === '') return [];

    // Create temp chess instance to parse PGN
    const tempChess = new Chess();
    if (initialFen && initialFen !== tempChess.fen()) {
      try {
        tempChess.load(initialFen);
      } catch (err) {
        console.error('Invalid FEN for parsing:', err);
      }
    }

    // Parse PGN string to extract moves
    // Format: "1. e4 e5 2. Nf3 Nc6" etc
    // Remove move numbers and split by whitespace to get all moves
    const cleanPgn = initialPgn
      .replace(/\d+\./g, '') // Remove move numbers like "1." "2."
      .trim()
      .split(/\s+/) // Split by whitespace
      .filter(move => move.length > 0 && !move.includes(':')); // Remove empty strings and metadata (Title:, FEN:, etc)

    // Validate and process each move
    const validMoves = [];
    for (const move of cleanPgn) {
      try {
        const result = tempChess.move(move);
        if (result) {
          validMoves.push({
            san: result.san,
            moveNumber: Math.floor(tempChess.history().length / 2) + (tempChess.history().length % 2 === 0 ? 0 : 1),
            color: result.color
          });
        }
      } catch (err) {
        console.error('Invalid move in PGN:', move);
        break; // Stop at first invalid move
      }
    }

    return validMoves;
  }, [initialPgn, initialFen]);

  // State
  const [position, setPosition] = useState(chess.fen());
  const [currentMoveIndex, setCurrentMoveIndex] = useState(-1); // -1 = start position
  const [isExploring, setIsExploring] = useState(false);
  const [explorationFen, setExplorationFen] = useState(null);
  const [movesRevealed, setMovesRevealed] = useState(showMoves); // For puzzle mode
  const [title, setTitle] = useState(initialTitle);
  const [showMovesEnabled, setShowMovesEnabled] = useState(showMoves);
  const [selectedSquare, setSelectedSquare] = useState(null); // For click-to-move
  const [fenInput, setFenInput] = useState(initialFen);
  const [pgnInput, setPgnInput] = useState(initialPgn);

  // Sync with prop changes (when parent updates the raw text)
  useEffect(() => {
    setFenInput(initialFen);
    try {
      chess.load(initialFen);
      setPosition(chess.fen());
    } catch (err) {
      console.error('Invalid FEN from parent:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFen]);

  useEffect(() => {
    setPgnInput(initialPgn);
  }, [initialPgn]);

  useEffect(() => {
    setTitle(initialTitle);
  }, [initialTitle]);

  useEffect(() => {
    setShowMovesEnabled(showMoves);
    setMovesRevealed(showMoves);
  }, [showMoves]);

  // Reset chess to start position
  const resetToStart = () => {
    chess.reset();
    if (initialFen && initialFen !== chess.fen()) {
      try {
        chess.load(initialFen);
      } catch (err) {
        console.error('Failed to load initial FEN:', err);
      }
    }
    setPosition(chess.fen());
  };

  // Navigate to specific move
  const navigateToMove = (moveIndex) => {
    resetToStart();

    // Apply moves up to moveIndex
    for (let i = 0; i <= moveIndex && i < parsedMoves.length; i++) {
      try {
        chess.move(parsedMoves[i].san);
      } catch (err) {
        console.error('Error applying move:', parsedMoves[i].san, err);
        break;
      }
    }

    setPosition(chess.fen());
    setCurrentMoveIndex(moveIndex);
    setIsExploring(false);
    setExplorationFen(null);
    setSelectedSquare(null); // Clear selection when navigating
  };

  // Navigation handlers
  const handleFirst = () => {
    navigateToMove(-1);
  };

  const handlePrevious = () => {
    if (currentMoveIndex >= 0) {
      navigateToMove(currentMoveIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentMoveIndex < parsedMoves.length - 1) {
      navigateToMove(currentMoveIndex + 1);
    }
  };

  const handleLast = () => {
    if (parsedMoves.length > 0) {
      navigateToMove(parsedMoves.length - 1);
    }
  };

  // Handle piece drop
  const onDrop = (sourceSquare, targetSquare) => {
    // Preview mode: disable dragging entirely
    if (mode === 'preview') {
      return false;
    }

    // Live mode: allow exploration
    if (mode === 'live') {
      // If not exploring yet, save current position as exploration start
      if (!isExploring) {
        setIsExploring(true);
        // Chess instance is already at currentMoveIndex, so we start from there
      }

      try {
        const move = chess.move({
          from: sourceSquare,
          to: targetSquare,
          promotion: 'q' // Always promote to queen for simplicity
        });

        if (move) {
          setPosition(chess.fen());
          setExplorationFen(chess.fen());
          return true;
        }
      } catch (err) {
        return false;
      }

      return false;
    }

    // In edit mode, make the move and update PGN
    if (mode === 'edit') {
      try {
        const move = chess.move({
          from: sourceSquare,
          to: targetSquare,
          promotion: 'q'
        });

        if (move) {
          const newFen = chess.fen();
          setPosition(newFen);

          // DO NOT update fenInput - it should stay as the starting position
          // The FEN represents where the position STARTS, not where it currently is

          // Generate new PGN (strip headers, just moves)
          const fullPgn = chess.pgn();
          const movesOnly = extractMovesFromPgn(fullPgn);

          // Update PGN input state
          setPgnInput(movesOnly);

          // Update parent with PGN, title, showMoves
          // Keep FEN as the STARTING position (initialFen), not current position
          if (onChange) {
            onChange({ pgn: movesOnly, title, showSolution: showMovesEnabled, fen: initialFen });
          }

          // Update current move index
          setCurrentMoveIndex(chess.history().length - 1);

          return true;
        }
      } catch (err) {
        return false;
      }

      return false;
    }

    return false;
  };

  // Handle square click for click-to-move
  const onSquareClick = (square) => {
    // Preview mode: disable all interactions
    if (mode === 'preview') {
      return;
    }

    // If no square is selected, select this square (if it has a piece)
    if (!selectedSquare) {
      const piece = chess.get(square);
      if (piece) {
        // Only allow selecting pieces of the current turn color
        if (piece.color === chess.turn()) {
          setSelectedSquare(square);
        }
      }
      return;
    }

    // If clicking the same square, deselect it
    if (selectedSquare === square) {
      setSelectedSquare(null);
      return;
    }

    // Try to make a move from selectedSquare to clicked square
    const moveAttempt = {
      from: selectedSquare,
      to: square,
      promotion: 'q' // Always promote to queen
    };

    // Live mode: exploration
    if (mode === 'live') {
      if (!isExploring) {
        setIsExploring(true);
      }

      try {
        const move = chess.move(moveAttempt);
        if (move) {
          setPosition(chess.fen());
          setExplorationFen(chess.fen());
          setSelectedSquare(null); // Clear selection after successful move
        } else {
          // Invalid move - check if clicking another piece to select it instead
          const piece = chess.get(square);
          if (piece && piece.color === chess.turn()) {
            setSelectedSquare(square);
          } else {
            setSelectedSquare(null);
          }
        }
      } catch (err) {
        // Invalid move - check if clicking another piece
        const piece = chess.get(square);
        if (piece && piece.color === chess.turn()) {
          setSelectedSquare(square);
        } else {
          setSelectedSquare(null);
        }
      }
      return;
    }

    // Edit mode: make move and update PGN
    if (mode === 'edit') {
      try {
        const move = chess.move(moveAttempt);
        if (move) {
          const newFen = chess.fen();
          setPosition(newFen);

          // DO NOT update fenInput - it should stay as the starting position

          // Generate new PGN (strip headers, just moves)
          const fullPgn = chess.pgn();
          const movesOnly = extractMovesFromPgn(fullPgn);

          // Update PGN input state
          setPgnInput(movesOnly);

          // Update parent with PGN, title, showMoves
          // Keep FEN as starting position (initialFen)
          if (onChange) {
            onChange({ pgn: movesOnly, title, showSolution: showMovesEnabled, fen: initialFen });
          }

          // Update current move index
          setCurrentMoveIndex(chess.history().length - 1);
          setSelectedSquare(null); // Clear selection after successful move
        } else {
          // Invalid move - check if clicking another piece to select it instead
          const piece = chess.get(square);
          if (piece && piece.color === chess.turn()) {
            setSelectedSquare(square);
          } else {
            setSelectedSquare(null);
          }
        }
      } catch (err) {
        // Invalid move - check if clicking another piece
        const piece = chess.get(square);
        if (piece && piece.color === chess.turn()) {
          setSelectedSquare(square);
        } else {
          setSelectedSquare(null);
        }
      }
    }
  };

  // Undo last move (edit mode only)
  const handleUndo = () => {
    if (mode !== 'edit') return;

    const lastMove = chess.undo();
    if (lastMove) {
      const newFen = chess.fen();
      setPosition(newFen);

      // Update FEN input state
      setFenInput(newFen);

      // Generate new PGN (strip headers, just moves)
      const fullPgn = chess.pgn();
      const movesOnly = extractMovesFromPgn(fullPgn);

      // Update PGN input state
      setPgnInput(movesOnly);

      // Update parent with PGN, title, and showMoves
      if (onChange) {
        onChange({ pgn: movesOnly, title, showSolution: showMovesEnabled });
      }

      // Update current move index
      setCurrentMoveIndex(chess.history().length - 1);
      setSelectedSquare(null); // Clear selection when undoing
    }
  };

  // Handle title change (edit mode only)
  const handleTitleChange = (newTitle) => {
    setTitle(newTitle);
    if (mode === 'edit' && onChange) {
      const fullPgn = chess.pgn();
      const movesOnly = extractMovesFromPgn(fullPgn);
      onChange({ pgn: movesOnly, title: newTitle, showSolution: showMovesEnabled, fen: initialFen });
    }
  };

  // Handle Show Moves toggle (edit mode only)
  const handleShowMovesToggle = () => {
    const newShowMoves = !showMovesEnabled;
    setShowMovesEnabled(newShowMoves);
    setMovesRevealed(newShowMoves); // Update whether moves are visible
    if (mode === 'edit' && onChange) {
      const fullPgn = chess.pgn();
      const movesOnly = extractMovesFromPgn(fullPgn);
      onChange({ pgn: movesOnly, title, showSolution: newShowMoves, fen: initialFen });
    }
  };

  // Handle FEN input change (edit mode only)
  const handleFenChange = (newFen) => {
    setFenInput(newFen);
    try {
      const tempChess = new Chess();
      tempChess.load(newFen);
      // Valid FEN - apply it
      chess.load(newFen);
      const updatedFen = chess.fen();
      setPosition(updatedFen);
      setCurrentMoveIndex(-1);
      // Notify parent immediately with FEN change
      if (mode === 'edit' && onChange) {
        const fullPgn = chess.pgn();
        const movesOnly = extractMovesFromPgn(fullPgn);
        onChange({ pgn: movesOnly, title, showSolution: showMovesEnabled, fen: updatedFen });
      }
    } catch (err) {
      // Invalid FEN - just update input field
      console.error('Invalid FEN:', err);
    }
  };

  // Handle PGN input change (edit mode only)
  const handlePgnChange = (newPgn) => {
    setPgnInput(newPgn);
    if (mode === 'edit' && onChange) {
      // Keep FEN as starting position when PGN changes
      onChange({ pgn: newPgn, title, showSolution: showMovesEnabled, fen: initialFen });
    }
  };

  // Reset to main line (preview/live mode only)
  const handleReset = () => {
    if (mode === 'edit') return;

    // Navigate to last saved move
    navigateToMove(currentMoveIndex);
    setIsExploring(false);
    setExplorationFen(null);
    setSelectedSquare(null); // Clear selection when resetting
  };

  // Board size - responsive
  const boardWidth = isMobile ? 260 : 300;

  return (
    <div className={`my-2 ${isMobile ? 'p-2' : 'p-4'} bg-gray-800 rounded-lg border-2 border-gray-700`}>
      <div style={{
        display: isMobile ? 'block' : 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '300px 1fr',
        gap: isMobile ? '1rem' : '1.5rem'
      }}>
        {/* LEFT COLUMN: Board and Navigation Controls */}
        <div style={{ position: 'static' }}>
          {/* Chessboard - NO transforms, NO positioning to avoid drag offset */}
          <div style={{ marginBottom: '1rem', maxWidth: isMobile ? '260px' : '300px', margin: isMobile ? '0 auto 1rem auto' : '0 0 1rem 0' }}>
            <Chessboard
              position={position}
              onPieceDrop={onDrop}
              onSquareClick={onSquareClick}
              boardWidth={boardWidth}
              customSquareStyles={{
                ...(selectedSquare && {
                  [selectedSquare]: {
                    backgroundColor: 'rgba(255, 255, 0, 0.4)'
                  }
                })
              }}
            />
          </div>

          {/* Navigation controls */}
          {!hideControls && (
            <div className="flex items-center justify-between mb-3" style={{ width: isMobile ? '260px' : '300px', margin: isMobile ? '0 auto 0.75rem auto' : '0 0 0.75rem 0' }}>
              {/* Mode-specific button */}
              {mode === 'edit' ? (
                <button
                  onClick={handleUndo}
                  disabled={chess.history().length === 0}
                  className={`${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} bg-red-900 text-red-200 rounded hover:bg-red-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors`}
                  title="Undo last move"
                >
                  <i className="fas fa-trash "></i>
                </button>
              ) : (
                <button
                  onClick={handleReset}
                  disabled={!isExploring}
                  className={`${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} bg-blue-900 text-blue-200 rounded hover:bg-blue-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors`}
                  title="Reset to main line"
                >
                  <i className="fas fa-rotate-left"></i>
                </button>
              )}

              <button
                onClick={handleFirst}
                disabled={currentMoveIndex === -1}
                className={`${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors`}
                title="First move"
              >
                <i className="fas fa-fast-backward"></i>
              </button>

              <button
                onClick={handlePrevious}
                disabled={currentMoveIndex === -1}
                className={`${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors`}
                title="Previous move"
              >
                <i className="fas fa-step-backward"></i>
              </button>

              <button
                onClick={handleNext}
                disabled={currentMoveIndex === parsedMoves.length - 1}
                className={`${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors`}
                title="Next move"
              >
                <i className="fas fa-step-forward"></i>
              </button>

              <button
                onClick={handleLast}
                disabled={currentMoveIndex === parsedMoves.length - 1}
                className={`${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors`}
                title="Last move"
              >
                <i className="fas fa-fast-forward"></i>
              </button>
            </div>
          )}

        

         
        </div>

        {/* RIGHT COLUMN: Title and Moves */}
        <div className="flex-1">
          {/* Title */}
          <div className="mb-2">
            {mode === 'edit' ? (
              <>
                <label className="block text-xs text-gray-400 mb-1">Board Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Board Title (optional)"
                  className="w-full px-2 py-1 bg-gray-700 text-gray-200 border border-gray-600 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </>
            ) : (
              title && (
                <h3 className="text-lg font-semibold text-gray-200">
                  {title}
                </h3>
              )
            )}
          </div>

          {/* FEN Input (Edit mode only) */}
          {mode === 'edit' && (
            <div className="mb-2">
              <label className="block text-xs text-gray-400 mb-1">FEN Position</label>
              <input
                type="text"
                value={fenInput}
                onChange={(e) => handleFenChange(e.target.value)}
                placeholder="rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
                className="w-full px-2 py-1 bg-gray-700 text-gray-200 border border-gray-600 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          )}

          {/* PGN Input (Edit mode only) */}
          {mode === 'edit' && (
            <div className="mb-2">
              <label className="block text-xs text-gray-400 mb-1">PGN Moves</label>
              <input
                type="text"
                value={pgnInput}
                onChange={(e) => handlePgnChange(e.target.value)}
                placeholder="1. e4 e5 2. Nf3 Nc6"
                className="w-full px-2 py-1 bg-gray-700 text-gray-200 border border-gray-600 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Show Moves Toggle (Edit mode only) */}
          {mode === 'edit' && (
            <div className="mb-4">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showMovesEnabled}
                  onChange={handleShowMovesToggle}
                  className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-2 focus:ring-blue-500"
                />
                <span>Show Moves (reveal moves immediately)</span>
              </label>
            </div>
          )}

          {/* Exploration indicator */}
          {isExploring && (
            <div className="mb-3">
              <span className="inline-block px-3 py-1 bg-blue-900 text-blue-200 rounded-full text-xs font-semibold">
                <i className="fas fa-compass mr-1"></i>
                Exploring variations
              </span>
            </div>
          )}

          {/* Move buttons or Show Moves button */}
          {parsedMoves.length > 0 && (
            <div>
              {movesRevealed ? (
                <div className="flex flex-wrap gap-1">
                  {parsedMoves.map((move, index) => {
                    const isActive = index === currentMoveIndex;
                    const moveNumberDisplay = move.color === 'w'
                      ? `${move.moveNumber}. ${move.san}`
                      : `${move.san}`;

                    return (
                      <button
                        key={index}
                        onClick={() => navigateToMove(index)}
                        className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-sm'} rounded font-mono transition-colors ${
                          isActive
                            ? 'bg-green-700 text-white font-bold'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {moveNumberDisplay}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <button
                  onClick={() => setMovesRevealed(true)}
                  className={`${isMobile ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'} bg-blue-900 text-blue-200 rounded hover:bg-blue-800 transition-colors font-semibold`}
                >
                  <i className="fas fa-chess-knight mr-2"></i>
                  Show Moves
                </button>
              )}
            </div>
          )}
          {parsedMoves.length === 0 && mode === 'edit' && (
               <div className="flex items-center gap-2 text-sm text-gray-300">
               <span>Make moves to begin.</span>
             </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogChessBoard;
